import { useRouter } from 'next/router'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import BaseModal from 'components/BaseModal'
import Button from 'components/Button'
import { type DatePickerDate, DatePickerInput } from 'components/Input/DatePicker'
import Spacer from 'components/Spacer'
import { FlexDiv, FlexDivColCentered } from 'components/layout/flex'
import { setOpenModal } from 'state/app/reducer'
import { setUserInfoTableFilter } from 'state/futures/reducer'
import { selectUserInfoTableFilter } from 'state/futures/selectors'
import type { TableFilteredFuturesTabs } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import { type TableFilter, type TimeFilterOptions, TimeFilterValues } from './TableFilters'
import TransactionsDropDown, { type TransactionsHistoryOptionsType } from './TypeFilterDropDown'

const ShowFilterType = ['transaction_history', 'order_history'] as const

type TimeFilterSpanProps = {
	onChange: (span: TimeFilterOptions) => void
	span?: TimeFilterOptions
}

const TableFilterModal = () => {
	const { t } = useTranslation()
	const router = useRouter()
	const { tab } = router.query as { tab: TableFilteredFuturesTabs }
	const dispatch = useAppDispatch()
	const tableFilter = useAppSelector(selectUserInfoTableFilter(tab))

	const [showFilterType, setShowFilterType] = useState<boolean>(false)
	const [typeFilter, setTypeFilter] = useState<TableFilter['type'] | undefined>()
	const [spanFilter, setSpanFilter] = useState<TimeFilterOptions | undefined>(tableFilter?.span)
	const [dateFilter, setDateFilter] = useState<DatePickerDate | undefined>({
		from: tableFilter?.from,
		to: tableFilter?.to,
	})

	const dropDownType = useMemo(() => {
		if (tab === 'transaction_history') {
			return 'transactions'
		} else if (tab === 'order_history') {
			return 'orders'
		}
	}, [tab])

	useEffect(() => {
		if (tab && ShowFilterType.includes(tab as (typeof ShowFilterType)[number])) {
			setShowFilterType(true)
		}
	}, [tab])

	const onClose = useCallback(() => dispatch(setOpenModal(null)), [dispatch])

	const handleOnDatePickerChange = useCallback((date: Date[]) => {
		setDateFilter({ from: date[0].toString(), to: date[1].toString() })
		setSpanFilter(undefined)
	}, [])

	const handleOnReset = useCallback(() => {
		dispatch(setUserInfoTableFilter({ tableFilter: null, futuresTab: tab }))
		onClose()
	}, [dispatch, tab, onClose])

	const handleOnSpanChange = useCallback((span: TimeFilterOptions) => {
		setSpanFilter(span)
		setDateFilter(undefined)
	}, [])

	const handleOnSearch = useCallback(() => {
		dispatch(
			setUserInfoTableFilter({
				tableFilter: {
					from: dateFilter?.from,
					to: dateFilter?.to,
					span: spanFilter,
					type: typeFilter,
				},
				futuresTab: tab,
			})
		)

		onClose()
	}, [tab, dispatch, dateFilter, onClose, spanFilter, typeFilter])

	const handleTypeFilterChange = useCallback((type: TransactionsHistoryOptionsType) => {
		setTypeFilter(type.value)
	}, [])

	return (
		<Modal onDismiss={onClose} title="Filter">
			{showFilterType && dropDownType && (
				<>
					<DropDownContainer>
						<TransactionsDropDown
							value={typeFilter ?? tableFilter?.type}
							onChange={handleTypeFilterChange}
							type={dropDownType}
							mobile
						/>
					</DropDownContainer>
					<Divider height={1} divider />
				</>
			)}
			<TimeFilterSpan span={spanFilter} onChange={handleOnSpanChange} />
			<DatePickerContainer>
				<DatePickerInput
					prevLabel2="to"
					type="modal"
					date={dateFilter}
					onChange={handleOnDatePickerChange}
				/>
			</DatePickerContainer>
			<Divider height={1} divider />
			<ButtonsContainer>
				<BaseButton variant="secondary" onClick={handleOnReset}>
					{t('futures.market.table-filter.reset')}
				</BaseButton>
				<BaseButton
					variant="solid-highlight"
					onClick={handleOnSearch}
					disabled={!spanFilter && (!dateFilter?.from || !dateFilter?.to) && !typeFilter}
				>
					{t('futures.market.table-filter.search')}
				</BaseButton>
			</ButtonsContainer>
		</Modal>
	)
}

const TimeFilterSpan: FC<TimeFilterSpanProps> = ({ span, onChange }) => {
	const options = useMemo(() => {
		return TimeFilterValues.map((s) => ({
			name: s,
			label: s,
			active: s === span,
			onClick: () => onChange(s),
		}))
	}, [span, onChange])

	return (
		<OptionsContainer>
			{options.map((option) => (
				<OptionButton
					size="small"
					key={option.name}
					onClick={option.onClick}
					active={option.active}
				>
					{option.label}
				</OptionButton>
			))}
		</OptionsContainer>
	)
}

const Divider = styled(Spacer)`
	width: 100%;
	margin-bottom: 15px;
`
const OptionsContainer = styled(FlexDiv)`
	display: flex;
	justify-content: space-between;
	width: 100%;
	gap: 10px;
`
const OptionButton = styled(Button)<{ active: boolean }>`
	height: 24px;
	background-color: ${(props) => props.theme.colors.selectedTheme.input.background};
	color: ${(props) =>
		props.active
			? props.theme.colors.selectedTheme.text.value
			: props.theme.colors.selectedTheme.text.label};
	font-weight: 400;
	${(props) =>
		props.active &&
		css`
			background-color: ${props.theme.colors.selectedTheme.button.fillHover};
		`}
	width: 100%;
`

const DatePickerContainer = styled(FlexDiv)`
	padding: 15px 0px;
	justify-content: space-between;
	align-items: center;
	width: 100%;
`

const ButtonsContainer = styled(FlexDiv)`
	width: 100%;
	justify-content: space-between;
	gap: 10px;
	padding-top: 15px;
`
const Modal = styled(BaseModal)`
	& .card {
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.containers.primary.background};
	}
	& .card-body {
		align-items: center;
		padding: 10px 20px 30px 20px;
	}
	[data-reach-dialog-content] {
		max-width: 550px;
		position: absolute;
		top: 45%;
		left: 50%;
		transform: translate(-50%, -50%);
		margin-top: 0;
	}
`

const BaseButton = styled(Button)`
	font-size: 14px;
	width: 100%;
`

const DropDownContainer = styled(FlexDivColCentered)`
	width: 100%;
	padding-bottom: 15px;
`

export default TableFilterModal
